import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Alert } from '@osd/ui/alert';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Field } from '@osd/ui/field';
import { Input } from '@osd/ui/input';
import { Stack } from '@osd/ui/stack';
import { useAuth } from '../features/auth/AuthProvider';

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search['next'] === 'string' ? search['next'] : undefined,
  }),
});

/**
 * Validate the `next` URL we're about to send the user to after
 * sign-in. Anything that isn't a same-origin absolute path that
 * doesn't already contain a `?next=` collapses to '/'.
 */
export function safeNext(raw: string | undefined): string {
  if (raw === undefined || raw === '') return '/';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  let pathname: string;
  try {
    const u = new URL(raw, 'https://placeholder.invalid');
    pathname = u.pathname;
  } catch {
    return '/';
  }
  if (pathname === '/login' || pathname === '/register') return '/';
  if (/(?:^|[?&])next=/.test(raw)) return '/';
  return raw;
}

/**
 * Lightweight email format check — see register.tsx for the full
 * rationale. Duplicated here to keep each route file self-contained.
 */
export function validEmail(email: string): boolean {
  const at = email.indexOf('@');
  const dot = at >= 0 ? email.indexOf('.', at) : -1;
  return at > 0 && dot > at + 1 && dot !== email.length - 1;
}

/**
 * Inlined login form (post-composite-removal — root/osd#75).
 * Composes `@osd/ui/{Alert,Button,Field,Input,Stack}` directly.
 */
export function LoginFormInlined({
  onSubmit,
  error,
  footer,
  pending,
}: {
  readonly onSubmit: (v: { email: string; password: string }) => Promise<void> | void;
  readonly error: string | null;
  readonly footer?: ReactNode;
  readonly pending: boolean;
}): JSX.Element {
  return (
    <form
      className="osd-login-form"
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (pending) return;
        const fd = new FormData(e.currentTarget);
        const emailRaw = fd.get('email');
        const passwordRaw = fd.get('password');
        const email = typeof emailRaw === 'string' ? emailRaw.trim() : '';
        const password = typeof passwordRaw === 'string' ? passwordRaw : '';
        if (!email || !password) return;
        if (!validEmail(email)) return;
        void onSubmit({ email, password });
      }}
    >
      {error !== null ? <Alert variant="danger">{error}</Alert> : null}
      <Stack gap={3}>
        <Field label={<>Email</>} required={true}>
          <Input
            type="email"
            name="email"
            autoComplete="email"
            required={true}
            disabled={pending}
          />
        </Field>
        <Field label={<>Password</>} required={true}>
          <Input
            type="password"
            name="password"
            autoComplete="current-password"
            required={true}
            disabled={pending}
            minLength={12}
          />
        </Field>
      </Stack>
      <div className="osd-login-form__actions">
        <Button
          type="submit"
          variant="primary"
          className="osd-login-form__submit"
          disabled={pending}
        >
          {pending ? 'Signing in…' : 'Sign in'}
        </Button>
        {footer !== undefined ? <div className="osd-login-form__footer">{footer}</div> : null}
      </div>
    </form>
  );
}

function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate({ from: '/login' });
  const { next } = Route.useSearch();
  const [pending, setPending] = useState(false);

  // Post-login redirect — fires in a useEffect rather than inside
  // handleSubmit. The naive `await auth.login(); await navigate(…)`
  // sequence races the React commit. Watching auth.user here lets
  // the redirect run after React has flushed the state change.
  useEffect(() => {
    if (auth.user === null) return;
    if (pending) return;
    void navigate({ to: safeNext(next) });
  }, [auth.user, pending, next, navigate]);

  async function handleSubmit(values: { email: string; password: string }): Promise<void> {
    setPending(true);
    try {
      await auth.login(values);
    } catch {
      // auth.error is already set by the provider.
    } finally {
      setPending(false);
    }
  }

  const showDemoHint = import.meta.env.DEV;

  return (
    <div className="osd-login-shell">
      {showDemoHint ? (
        <Alert variant="info">
          <strong>Demo credentials (dev only):</strong> <code>admin@osd.local</code> /{' '}
          <code>ChangeMe-Admin-2026!</code>
        </Alert>
      ) : null}
      <Card>
        <Stack gap={4}>
          <LoginFormInlined
            onSubmit={handleSubmit}
            pending={pending}
            error={auth.error}
            footer={
              <>
                <Link to="/register">Create account</Link>
                <a href="/forgot">Forgot password?</a>
              </>
            }
          />
        </Stack>
      </Card>
    </div>
  );
}
